/**
 * Jargon Prompt section for the Test Suite Configuration page.
 *
 * Displays an expandable section with a multi-line textarea for the
 * LLM-generated jargon prompt. The prompt teaches the polisher LLM
 * how to translate raw parameter values into natural business terminology.
 * Includes a character count indicator below the textarea.
 *
 * @see Requirements 7.1–7.4
 */

import React from 'react';
import {
  ExpandableSection,
  SpaceBetween,
  FormField,
  Textarea,
  Box,
} from '@amzn/awsui-components-react';
import { ConfigPageAction, ConfigPageState, ValidationErrors } from './types';

// ─── Constants ─────────────────────────────────────────────────────────

const JARGON_PROMPT_ROWS = 10;

// ─── Props ─────────────────────────────────────────────────────────────

export interface JargonPromptSectionProps {
  state: ConfigPageState;
  dispatch: React.Dispatch<ConfigPageAction>;
  validationErrors: ValidationErrors;
}

// ─── Component ─────────────────────────────────────────────────────────

const JargonPromptSection: React.FC<JargonPromptSectionProps> = ({
  state,
  dispatch,
  validationErrors,
}) => {
  const { jargonPrompt } = state;
  const characterCount = jargonPrompt.length;

  const handleChange = (value: string) => {
    dispatch({ type: 'UPDATE_FIELD', path: 'jargonPrompt', value });
  };

  return (
    <ExpandableSection
      headerText="Jargon Prompt"
      variant="container"
      defaultExpanded
    >
      <SpaceBetween size="s">
        <FormField
          label="Jargon prompt"
          description="Instructions for the polisher LLM on how to translate raw parameter values into natural business terminology"
          errorText={validationErrors['jargonPrompt']}
          constraintText="This prompt is pre-populated by the LLM based on your business knowledge. Edit as needed to refine terminology guidance."
        >
          <Textarea
            value={jargonPrompt}
            onChange={({ detail }) => handleChange(detail.value)}
            rows={JARGON_PROMPT_ROWS}
            ariaLabel="Jargon prompt"
          />
        </FormField>

        <Box
          textAlign="right"
          color="text-status-inactive"
          fontSize="body-s"
        >
          {characterCount.toLocaleString()} characters
        </Box>
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default JargonPromptSection;
